<script setup>

import moment from 'moment';
import { computed, onMounted, ref, watch } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({
  presenceHour: Date,
  presenceType: String,
  presenceLastType: String,
  index: Number,
  myClass: String,
  disabled: Boolean,
  minimumTime: Date,
  maximumTime: Date,
})
// eslint-disable-next-line no-undef
const emit = defineEmits(['changeInput', 'changeType', 'invalidInput'])

const refInput = ref(null);
const newTime = ref();
const oldTime = ref();
const editTimeValue = ref();
const changeHour = ref(false);
const disabled2 = ref(false);

const myClass = computed(() => {
  return props.myClass;
})

watch(
  () => props.presenceType,
  (newValue) => {
    selectedValue.value = newValue;
  }
);

watch(
  () => props.disabled,
  (newValue) => {
    disabled2.value = newValue;
  },
  { immediate: true }
);

// eslint-disable-next-line no-unused-vars
const presenceTypeOptions = computed(() => {
  const typeLabels = { entry: 'Entrada', exit: 'Salida', pause: 'Parada', resume: 'Reanudación' };
  const currentType = props.presenceType;
  const type = props.presenceLastType;

  if (currentType && !type) {
    return [{ value: currentType, label: typeLabels[currentType] || currentType }];
  }

  let options = [];
  if (!type) {
    options = [{ value: 'entry', label: 'Entrada' }];
  } else if (type === 'entry' || type === 'resume') {
    options = [{ value: 'exit', label: 'Salida' }, { value: 'pause', label: 'Parada' }];
  } else if (type === 'pause') {
    options = [{ value: 'resume', label: 'Reanudación' }];
  } else if (type === 'exit') {
    options = [{ value: 'entry', label: 'Entrada' }];
  }

  // Si el tipo actual no está entre las opciones (p.ej. tras reordenación), añadirlo primero
  if (currentType && !options.find(o => o.value === currentType)) {
    options = [{ value: currentType, label: typeLabels[currentType] || currentType }, ...options];
  }

  return options;
});

const selectedValue = ref(null);


onMounted(() => {
  
  if (props.presenceType) {
    selectedValue.value = props.presenceType;
  }

  if (props.presenceHour == null) {
        editTimeValue.value = '';
        oldTime.value = '';
        return;
    }
    editTimeValue.value = formatHourDate(props.presenceHour);
    oldTime.value = editTimeValue.value;
})

const focusInput = () => {
  if (disabled2.value) return;
  if (editTimeValue.value === '') {
    return;
  }
  let temp = editTimeValue.value.split(':');
  editTimeValue.value = temp[0] + temp[1];
  refInput.value.select();
}

const blurInput = () => {
  if (disabled2.value) return;
  const normalized = parseToHHMM(editTimeValue.value);
  if (!normalized) return; // si es inválido, no tocar
  newTime.value = normalized;
  editTimeValue.value = normalized;
}

const changeInput = async () => {
  if (disabled2.value) return;
  changeHour.value = true;
  await checkAndChangeInput();
}

const checkAndChangeInput = async () => {
  if (disabled2.value) return;
  if (!changeHour.value) {
    refInput.value?.blur?.();
    return;
  }

  const normalized = parseToHHMM(editTimeValue.value);
  if (!normalized) {
    changeHour.value = false;
    return;
  }

  if (!isWithinSequentialTimeRange(normalized)) {
    editTimeValue.value = oldTime.value || '';
    changeHour.value = false;
    return;
  }

  newTime.value = normalized;
  editTimeValue.value = normalized;
  oldTime.value = normalized;
  changeHour.value = false;

  if (props.index !== undefined) {
    emit('changeInput', { sign: editTimeValue.value, index: props.index });
    return;
  }
  emit('changeInput', editTimeValue.value);
}

const parseToHHMM = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return null;

  let hh = null, mm = null;

  if (s.includes(':')) {
    const m = s.match(/^(\d{1,2}):(\d{1,2})$/);
    if (m) {
      hh = parseInt(m[1], 10);
      mm = parseInt(m[2], 10);
    }
  } else if (/^\d+$/.test(s)) {
    if (s.length <= 2) {
      hh = parseInt(s, 10); mm = 0;
    } else if (s.length === 3) {
      hh = parseInt(s.slice(0, 1), 10);
      mm = parseInt(s.slice(1), 10);
    } else if (s.length === 4) {
      hh = parseInt(s.slice(0, 2), 10);
      mm = parseInt(s.slice(2), 10);
    } else {
      return null;
    }
  } else {
    return null;
  }

  if (
    hh == null || mm == null ||
    Number.isNaN(hh) || Number.isNaN(mm) ||
    hh < 0 || hh > 23 || mm < 0 || mm > 59
  ) return null;

  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const formatHourDate = (date) => {
  return moment(date).format('HH:mm')
}

const timeToMinutes = (time) => (time.getHours() * 60) + time.getMinutes();

const isWithinSequentialTimeRange = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  const currentTime = (hours * 60) + minutes;

  if (props.minimumTime && currentTime <= timeToMinutes(props.minimumTime)) {
    emit('invalidInput', 'La hora debe ser posterior al fichaje anterior.');
    return false;
  }

  if (props.maximumTime && currentTime >= timeToMinutes(props.maximumTime)) {
    emit('invalidInput', 'La hora debe ser anterior al siguiente fichaje.');
    return false;
  }

  return true;
}

const handleChange = (value) => {
  emit('changeType', {
    type: value,
    index: props.index
  });
};

</script>

<template>
  <div>
    <el-input ref="refInput" v-model="editTimeValue" class="inputSign" @focus="focusInput" @blur="blurInput"
      @change="changeInput" :readonly="disabled2" :class="{
        'new': myClass === 'new',
        'modification': myClass === 'modification',
        'rejected': myClass === 'rejected',
        'disabled': props.disabled
      }"></el-input>
    <el-select placeholder="Tipo" style="width: 120px; margin-left: 5px;" v-model="selectedValue" @change="handleChange"
      :readonly="disabled" :disabled="disabled">
      <el-option v-for="type in presenceTypeOptions" :key="type.value" :label="type.label" :value="type.value" />
    </el-select>
  </div>
</template>

<style lang="scss" scoped>
.inputSign {
  width: 85px;
  margin-left: 7px;
}

.inputSign:deep(.el-input__inner) {
  text-align: center;
}

.modification:deep(.el-input__wrapper) {
  background-color: #e7e558;
}

.new:deep(.el-input__wrapper) {
  background-color: #99d68a;
}

.rejected:deep(.el-input__wrapper) {
  background-color: #ff0000;
}

.rejected:deep(.el-input__inner) {
  color: white;
}

.disabled:deep(.el-input__inner) {
  cursor: default;
  color: #7b7b7b;
}

.disabled:deep(.el-input__wrapper) {
  cursor: default;
  background-color: #f1f3f6;
}

.disabled:deep(.el-select__wrapper) {
  cursor: default;
  background-color: white !important;
}

.disabled:deep(.el-select__inner) {
  cursor: default;
  background-color: white !important;
}

:deep(.el-select__suffix) {
  display: none;
}
</style>
